<script lang="ts" setup>import { computed as _computed } from 'vue';

import type { GreetingCardStorySlide } from '@consumer/types/serializers'

withDefaults(defineProps<{
  slide: GreetingCardStorySlide
}>(), {  })

const photoUrl = _computed(() => {
  if (__props.slide.base64Photo) return __props.slide.base64Photo
  if (typeof __props.slide.photo === 'object' && __props.slide.photo?.url) return __props.slide.photo.url
  if (typeof __props.slide.photo === 'string') return __props.slide.photo
  return ''
})
</script>

<template>
  <div class="page-photo">
    <!-- Heavily blurred copy of the same photo, used as a filler
         background so the page never looks empty around the crisp
         foreground image. -->
    <div
      class="page-photo-bg"
      :style="{ backgroundImage: `url(${photoUrl})` }"
    />
    <img :src="photoUrl" :alt="slide.text || ''" class="page-photo-image">
    <div class="page-photo-caption">
      {{ slide.text }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.page-photo {
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;

  // Clip the scaled-up blurred bg so its edges don't bleed out.
  overflow: hidden;
  padding: 16px;
  position: relative;
  width: 100%;
}

// Blurred background — same photo, scaled up so the blurred soft edges
// fall outside the visible area, with a heavy filter for the "frosted
// glass" effect.
.page-photo-bg {
  background-position: center;
  background-size: cover;
  filter: blur(40px);
  inset: 0;
  position: absolute;
  transform: scale(1.2);
  z-index: 0;
}

// Cap the photo at 50% of viewport height so there's always room for the
// caption and the bottom nav bar without the image getting cropped/clipped.
.page-photo-image {
  border-radius: 24px;
  box-shadow: 0 4px 20px rgb(0 0 0 / 10%);
  height: 60dvh;
  max-width: 100%;

  // `cover` (not `contain`) is intentional: the image carries a
  // box-shadow, and `contain` would letterbox the image inside its
  // fixed 60dvh box, leaving the shadow tracing the outer box rather
  // than the visible photo. `cover` makes the photo fill the framed
  // box edge-to-edge so the shadow always hugs what the user sees.
  object-fit: cover;
  position: relative;
  z-index: 100;
}

.page-photo-caption {
  color: var(--font-color);
  font-family: var(--card-font-family, inherit);
  font-size: calc(var(--card-font-size, 25px) * 0.8);

  // Fallback weight stays 700 so captions read as bold when no font is
  // picked (was the hard-coded value before the catalogue existed).
  font-weight: var(--card-font-weight, 700);
  line-height: var(--card-line-height, 108%);
  margin-top: 25px;
  max-width: min(80vw, 600px);

  // Same delayed-fade-in / instant-fade-out behavior as NotePage —
  // both the value and the transition come from ReadingStage so the
  // fade direction switches based on the cover state.
  opacity: var(--cover-fully-open, 1);
  position: relative;
  text-align: center;
  transition: var(--cover-fully-open-transition, opacity 0.4s ease-out 0.3s);
  z-index: 100;
}
</style>
